import { observer } from 'mobx-react';
import { fromNullable } from 'maybeasy';
import { T } from '../../../../../../Translations';
import ProgramHours from '../ProgramOverview/ProgramHours';
import { IconButton, PenIcon, Typography } from '@execonline-inc/execonline-ui';
import { ExperienceResource } from '../Types';

export interface Props {
  experienceResource: ExperienceResource;
  selectedDateIds?: Set<number>;
  onEditDates?: () => void;
  editable?: boolean;
}

function ExperienceCardBody({
  experienceResource,
  selectedDateIds,
  onEditDates,
  editable = false,
}: Props) {
  const { title, competency_count, total_hours, offering_type, program_instance_options } =
    experienceResource.payload;
  const startDatesCount = program_instance_options.scheduled.length;
  const isScheduledProgram = offering_type === 'epc';
  // Show selected count if available, otherwise show total available dates.
  // DVLP-7865: only selected runs that still exist count. A selection made of
  // runs that have ended read "3 Dates" while the date modal had nothing
  // checked and sharing found nothing.
  const availableDateIds = new Set(program_instance_options.scheduled.map((date) => date.id));
  const displayCount = fromNullable(selectedDateIds)
    .map((ids) => Array.from(ids).filter((id) => availableDateIds.has(id)).length)
    .getOrElseValue(startDatesCount);

  return (
    <>
      <Typography
        className="mb-3 cursor-pointer"
        variant="subtitleMedium"
        as="h5"
        fontWeight="medium"
      >
        {title}
      </Typography>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-x-3 gap-y-1 empty:hidden sm:gap-x-6">
          <span className="text-foreground text-xs">
            <T kind={`{{count}} Competency`} count={competency_count} />
          </span>
          <ProgramHours hours={total_hours} />
          {isScheduledProgram && startDatesCount > 0 && (
            <div className="flex items-center">
              <span className="text-foreground text-xs">
                <T kind={`{{count}} Date`} count={displayCount} />
              </span>
              {editable && onEditDates && (
                <IconButton
                  onClick={onEditDates}
                  className="p-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-intercom-target="builder-v2-edit-dates-button"
                  aria-label="Edit program dates"
                >
                  <PenIcon size="xs" />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default observer(ExperienceCardBody);
